import { Dropdown, Empty, Tabs, type MenuProps, type TabsProps } from 'antd';
import { CopyOutlined, PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import { MethodBadge } from '@presentation/components/workbench/MethodBadge';
import { useWorkbenchStore, type WorkbenchTab } from '@presentation/stores/workbenchStore';

interface TabLabelProps {
  tab: WorkbenchTab;
}

const TabLabel = ({ tab }: TabLabelProps) => {
  const tabs = useWorkbenchStore((state) => state.tabs);
  const closeTab = useWorkbenchStore((state) => state.closeTab);
  const closeOtherTabs = useWorkbenchStore((state) => state.closeOtherTabs);
  const closeAllTabs = useWorkbenchStore((state) => state.closeAllTabs);
  const togglePin = useWorkbenchStore((state) => state.togglePin);
  const duplicateTab = useWorkbenchStore((state) => state.duplicateTab);
  const reorderTabs = useWorkbenchStore((state) => state.reorderTabs);

  const menuItems: MenuProps['items'] = [
    {
      key: 'pin',
      icon: tab.isPinned ? <PushpinFilled /> : <PushpinOutlined />,
      label: tab.isPinned ? 'Unpin' : 'Pin',
    },
    { key: 'duplicate', icon: <CopyOutlined />, label: 'Duplicate tab' },
    { key: 'close', label: 'Close' },
    { key: 'close-others', label: 'Close others' },
    { key: 'close-all', label: 'Close all' },
  ];

  const handleClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'pin') togglePin(tab.key);
    else if (key === 'duplicate') duplicateTab(tab.key);
    else if (key === 'close') closeTab(tab.key);
    else if (key === 'close-others') closeOtherTabs(tab.key);
    else if (key === 'close-all') closeAllTabs();
  };

  const handleDragStart = (event: React.DragEvent) => {
    event.dataTransfer.setData('text/plain', tab.key);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const sourceKey = event.dataTransfer.getData('text/plain');
    if (!sourceKey || sourceKey === tab.key) return;

    const fromIndex = tabs.findIndex((t) => t.key === sourceKey);
    const toIndex = tabs.findIndex((t) => t.key === tab.key);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...tabs];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    reorderTabs(reordered.map((t) => t.key));
  };

  return (
    <Dropdown menu={{ items: menuItems, onClick: handleClick }} trigger={['contextMenu']}>
      <span
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="flex max-w-[160px] cursor-grab items-center gap-1.5 active:cursor-grabbing"
      >
        {tab.isPinned && <PushpinFilled className="shrink-0 text-[10px] text-gray-400" />}
        <MethodBadge method={tab.draft.method} className="shrink-0" />
        <span className="min-w-0 truncate">{tab.draft.name || 'Untitled Request'}</span>
        {tab.isDirty && (
          <span className="shrink-0 text-amber-500" title="Unsaved changes">
            ●
          </span>
        )}
      </span>
    </Dropdown>
  );
};

interface RequestTabsBarProps {
  onNewTab: () => void;
}

export const RequestTabsBar = ({ onNewTab }: RequestTabsBarProps) => {
  const tabs = useWorkbenchStore((state) => state.tabs);
  const activeKey = useWorkbenchStore((state) => state.activeKey);
  const setActiveKey = useWorkbenchStore((state) => state.setActiveKey);
  const closeTab = useWorkbenchStore((state) => state.closeTab);

  if (tabs.length === 0) {
    return (
      <div className="flex h-12 items-center justify-center border-b border-black/10 dark:border-white/10">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          styles={{ image: { height: 20 } }}
          description={<span className="text-xs text-gray-400">No open requests</span>}
        />
      </div>
    );
  }

  const items: TabsProps['items'] = tabs.map((tab) => ({
    key: tab.key,
    label: <TabLabel tab={tab} />,
    closable: !tab.isPinned,
  }));

  const handleEdit: TabsProps['onEdit'] = (targetKey, action) => {
    if (action === 'add') {
      onNewTab();
    } else if (action === 'remove' && typeof targetKey === 'string') {
      closeTab(targetKey);
    }
  };

  return (
    <Tabs
      type="editable-card"
      hideAdd={false}
      items={items}
      activeKey={activeKey ?? undefined}
      onChange={setActiveKey}
      onEdit={handleEdit}
      size="small"
      className="workbench-tabs"
    />
  );
};
